import PageSeo from '../components/PageSeo';
import Newsletters from './Newsletters';
import { Link } from 'react-router-dom';
import '../styles/our-team.css';

const leaders = [
  {
    name: 'Kailash Chandra',
    alternateName: null,
    position: 'Founder, Managing Director & Chief Technology Officer (CTO)',
    statuses: ['Founder', 'Managing Director', 'CTO'],
    image: '/img/team/kailash-chandra.png',
    imageAlt: 'Kailash Chandra, Founder, Managing Director and CTO of Plux Infosystems',
    linkedin: 'https://www.linkedin.com/in/kailashprogrammer/',
    bio: [
      'Kailash Chandra is the Founder, Managing Director, and Chief Technology Officer of Plux Infosystems Private Limited. He leads the company’s vision, technology strategy, and global delivery operations.',
      'With over 14 years of experience in software engineering and digital transformation, Kailash specializes in designing and delivering enterprise-grade digital solutions for organizations across Australia, the United States, the Middle East, and Asia.',
      'He combines strategic business leadership with deep technical expertise, helping organizations modernize legacy systems, accelerate digital transformation, and build scalable, future-ready technology platforms.',
    ],
    expertiseTitle: 'Areas of Expertise',
    expertise: [
      'Enterprise Solution Architecture',
      'Digital Transformation & Technology Strategy',
      'Web Application Development',
      'Mobile Application Development (iOS & Android)',
      'Enterprise eCommerce Solutions',
      'AI & Intelligent Automation',
      'Cloud Architecture & DevOps',
      'API & Systems Integration',
      'Cybersecurity Best Practices',
      'Technical Due Diligence',
      'Engineering Leadership',
      'Product Strategy & Delivery',
    ],
    technologyTitle: 'Technology Expertise',
    technology: [
      'Adobe Commerce (Magento)',
      'Laravel',
      'WordPress',
      'Shopify',
      'React',
      'Next.js',
      'Node.js',
      'PHP',
      'Python',
      'AWS',
      'Microsoft Azure',
      'Docker',
      'Kubernetes',
      'OpenAI & Generative AI',
    ],
    deliveryTitle: 'Project, Delivery & Leadership',
    delivery: [
      'Agile (Scrum & Kanban)',
      'Jira',
      'Azure DevOps',
      'Trello',
      'Asana',
      'Microsoft Project',
      'GitHub Projects',
      'GitLab',
      'Bitbucket',
      'Slack',
      'Microsoft Teams',
      'Google Workspace',
      'Product Roadmapping',
      'Resource Planning',
      'Cross-functional Team Leadership',
      'Technical Documentation',
      'Requirements Analysis',
      'QA & UAT Coordination',
    ],
    closing:
      'Kailash works closely with executive stakeholders, product owners, engineering teams, and business leaders to deliver secure, scalable, and business-focused digital products that create measurable commercial outcomes while ensuring successful project delivery from strategy through implementation.',
  },
  {
    name: 'Laxmi Chandra',
    alternateName: 'Also known as Raj Kumari',
    position: 'Co-Founder, Director & Head of Product Design',
    statuses: ['Co-Founder', 'Director', 'Head of Product Design'],
    image: '/img/team/laxmi-chandra.png',
    imageAlt:
      'Laxmi Chandra, also known as Raj Kumari, Co-Founder, Director and Head of Product Design at Plux Infosystems',
    linkedin: 'https://www.linkedin.com/in/laxmi-chandra-964a96200/',
    bio: [
      'Laxmi Chandra is the Co-Founder, Director, and Head of Product Design at Plux Infosystems Private Limited. She leads product design, user experience, and frontend design implementation.',
      'She transforms complex business requirements into intuitive digital experiences that support user engagement, accessibility, and customer satisfaction. Her work connects design, technology, and business objectives so each solution is visually clear and commercially effective.',
      'Working alongside clients, product teams, and software engineers, Laxmi Chandra oversees the complete product design lifecycle—from research and strategy to production-ready interfaces.',
    ],
    expertiseTitle: 'Areas of Expertise',
    expertise: [
      'Product Design Strategy',
      'User Experience (UX)',
      'User Interface (UI)',
      'Customer Experience (CX)',
      'Design Systems',
      'User Research',
      'Information Architecture',
      'Accessibility (WCAG)',
      'Responsive Web Design',
      'Frontend Design Implementation',
      'Mobile App Design',
      'Design Quality Assurance',
    ],
    technologyTitle: 'Technology Expertise',
    technology: [
      'Figma',
      'Adobe XD',
      'HTML5',
      'CSS3',
      'JavaScript',
      'Tailwind CSS',
      'React',
      'WordPress',
      'Shopify',
      'Mobile Interface Design',
    ],
    deliveryTitle: 'Design Operations & Delivery',
    delivery: [
      'Design Discovery Workshops',
      'UX Research Planning',
      'User Journey Mapping',
      'Wireframing & Prototyping',
      'Design System Governance',
      'Accessibility Reviews',
      'Responsive Design Reviews',
      'Design Handoff',
      'Frontend Design QA',
      'Usability Testing',
      'Stakeholder Presentations',
      'Product Roadmapping',
      'Requirements Analysis',
      'QA & UAT Coordination',
      'Jira',
      'Trello',
      'Slack',
      'Microsoft Teams',
    ],
    closing:
      'Laxmi Chandra helps ensure every digital product meets high standards of usability, accessibility, consistency, and performance. She collaborates with cross-functional teams to deliver effective digital experiences on time and in alignment with business objectives.',
  },
];

const LinkedInIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24">
    <path d="M5.2 3.5A1.7 1.7 0 1 1 5.2 7a1.7 1.7 0 0 1 0-3.5ZM3.7 8.4h3v9.9h-3V8.4Zm5 0h2.9v1.4h.1c.4-.8 1.4-1.7 2.9-1.7 3.1 0 3.7 2 3.7 4.7v5.5h-3v-4.9c0-1.2 0-2.7-1.7-2.7s-1.9 1.3-1.9 2.6v5h-3V8.4Z" />
  </svg>
);

const SkillGroup = ({ title, items }: { title: string; items: string[] }) => (
  <div className="leadership-skills">
    <h4>{title}</h4>
    <ul>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  </div>
);

const OurTeam = () => {
  return (
    <>
      <PageSeo path="/leadership" />
      <main className="leadership-page">
        <div className="header-spacer" />

        <section className="leadership-hero" aria-labelledby="leadership-title">
          <div className="leadership-container">
            <nav className="leadership-breadcrumbs" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span>Leadership</span>
            </nav>
            <p className="leadership-eyebrow">Plux Infosystems Private Limited</p>
            <h1 id="leadership-title">Executive Leadership</h1>
            <p className="leadership-intro">
              Visionary leadership, user-centric design, and engineering excellence—guiding digital
              products from concept to deployment.
            </p>
          </div>
        </section>

        <section className="leadership-profiles" aria-labelledby="directors-title">
          <div className="leadership-container">
            <div className="leadership-section-heading">
              <p className="leadership-eyebrow">Leadership team</p>
              <h2 id="directors-title">Who leads Plux Infosystems</h2>
              <p className="leadership-section-copy">
                Our founders combine technology strategy, product design, and delivery leadership to
                build scalable, secure, and commercially effective digital solutions.
              </p>
            </div>

            <div className="leadership-grid">
              {leaders.map((leader) => (
                <article className="leadership-card" key={leader.name}>
                  <div className="leadership-card__intro">
                    <div className="leadership-card__portrait">
                      <div className="leadership-card__portrait-glow" aria-hidden="true" />
                      <div className="leadership-card__portrait-frame">
                        <img src={leader.image} alt={leader.imageAlt} loading="lazy" />
                      </div>
                    </div>

                    <div className="leadership-card__summary">
                      <p className="leadership-card__position">{leader.position}</p>
                      <h3>{leader.name}</h3>
                      {leader.alternateName ? (
                        <p className="leadership-card__alternate-name">{leader.alternateName}</p>
                      ) : null}
                      <ul className="leadership-status-list" aria-label={`${leader.name} roles`}>
                        {leader.statuses.map((status) => (
                          <li className="leadership-status" key={status}>
                            {status}
                          </li>
                        ))}
                      </ul>
                      <div className="leadership-card__bio">
                        {leader.bio.map((paragraph) => (
                          <p key={paragraph}>{paragraph}</p>
                        ))}
                      </div>
                      <a
                        className="leadership-linkedin"
                        href={leader.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`View ${leader.name} on LinkedIn`}
                      >
                        <LinkedInIcon />
                        View LinkedIn profile
                      </a>
                    </div>
                  </div>

                  <div className="leadership-card__details">
                    <SkillGroup title={leader.expertiseTitle} items={leader.expertise} />
                    <SkillGroup title={leader.technologyTitle} items={leader.technology} />
                    <SkillGroup title={leader.deliveryTitle} items={leader.delivery} />
                    <p className="leadership-card__closing">{leader.closing}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="leadership-principles" aria-labelledby="leadership-principles-title">
          <div className="leadership-container leadership-principles__inner">
            <div>
              <p className="leadership-eyebrow">Leadership philosophy</p>
              <h2 id="leadership-principles-title">Leadership built around clarity and outcomes</h2>
            </div>
            <div className="leadership-principles__copy">
              <p>
                At Plux Infosystems, we believe that exceptional digital products are created through
                the combination of visionary leadership, user-centric design, and engineering
                excellence.
              </p>
              <p>
                Our leadership team partners with clients from concept to deployment, combining
                strategic consulting, product innovation, and technical expertise to deliver
                scalable, secure, and high-performing solutions that generate measurable business
                value.
              </p>
              <p className="leadership-principles__quote">
                Technology should solve business problems—not create complexity.
              </p>
            </div>
          </div>
        </section>

        <Newsletters />
      </main>
    </>
  );
};

export default OurTeam;
